import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { Receipt } from '@/types';

export function useReceipts() {
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: queryError } = await supabase
        .from('receipts')
        .select('*, projects(*, clients(*))')
        .order('receipt_date', { ascending: false })
        .order('created_at', { ascending: false });
      if (queryError) throw queryError;
      setReceipts((data as Receipt[]) || []);
    } catch {
      setReceipts([]);
      setError('No fue posible cargar los recibos. Verifique su conexión e intente nuevamente.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { receipts, loading, error, reload: load };
}

export function useProjectReceipts(projectId?: string) {
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!projectId) {
      setReceipts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data, error: queryError } = await supabase
        .from('receipts')
        .select('*')
        .eq('project_id', projectId)
        .order('receipt_date', { ascending: false });
      if (queryError) throw queryError;
      setReceipts((data as Receipt[]) || []);
    } catch {
      setReceipts([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  return { receipts, loading, reload: load };
}

/**
 * Devuelve un mapa proyectoId -> fechas de recibo, útil para reconocer el
 * estado PAGADO del cobro mensual sin cargar los recibos completos.
 */
export function useProjectReceiptDates() {
  const [receiptDates, setReceiptDates] = useState<Record<string, string[]>>({});

  const load = useCallback(async () => {
    try {
      const { data, error: queryError } = await supabase
        .from('receipts')
        .select('project_id, receipt_date');
      if (queryError) throw queryError;
      const map: Record<string, string[]> = {};
      (data || []).forEach((row: { project_id: string | null; receipt_date: string }) => {
        if (!row.project_id) return;
        if (!map[row.project_id]) map[row.project_id] = [];
        map[row.project_id].push(row.receipt_date);
      });
      setReceiptDates(map);
    } catch {
      setReceiptDates({});
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { receiptDates, reload: load };
}